import { api } from '../config/api';

export type DocumentType = 'unknown' | 'invoice' | 'bill' | 'notice' | 'payment_confirmation' | 'signup_notice' | 'maintenance_notice' | 'violation_notice' | 'meeting_notice' | 'newsletter' | 'other';

export interface HOAEmail {
  id: number;
  sender_email: string;
  sender_name?: string;
  recipient_email: string;
  subject: string;
  email_text?: string;
  email_html?: string;
  email_raw?: string;
  received_at: string;
  status: 'pending' | 'assigned' | 'processed' | 'ignored';
  priority: 'low' | 'medium' | 'high' | 'urgent';
  document_type: DocumentType;
  property_id?: number;
  assigned_by?: number;
  assigned_at?: string;
  ai_suggested_property_id?: number;
  ai_confidence_score?: string;
  ai_reasoning?: string;
  ai_processed_at?: string;
  requires_review: boolean;
  reviewed_by?: number;
  reviewed_at?: string;
  review_notes?: string;
  has_attachments: boolean;
  attachment_count: number;
  email_metadata?: any;
  created_at: string;
  updated_at?: string;
  property_name?: string;
  property_address?: string;
  ai_suggested_property_name?: string;
  assigned_by_username?: string;
  reviewed_by_username?: string;
}

export interface HOAEmailListResponse {
  emails: HOAEmail[];
  total: number;
  page: number;
  page_size: number;
  total_pages: number;
}

export interface HOAEmailStats {
  total_emails: number;
  pending_emails: number;
  assigned_emails: number;
  processed_emails: number;
  requires_review: number;
  by_priority: Record<string, number>;
  by_property: Record<string, number>;
}

export interface PropertyEmailAssignment {
  property_id: number;
  property_name: string;
  email_count: number;
  last_email_date?: string;
}

export interface HOAEmailAssign {
  property_id: number;
  review_notes?: string;
}

export interface HOAEmailUpdate {
  property_id?: number;
  status?: 'pending' | 'assigned' | 'processed' | 'ignored';
  priority?: 'low' | 'medium' | 'high' | 'urgent';
  document_type?: DocumentType;
  review_notes?: string;
  requires_review?: boolean;
}

export const hoaEmailsService = {
  // Get all HOA emails with filtering and pagination
  async getHOAEmails(params?: {
    skip?: number;
    limit?: number;
    status?: string;
    priority?: string;
    document_type?: string;
    property_id?: number;
    requires_review?: boolean;
    search?: string;
  }): Promise<HOAEmailListResponse> {
    const searchParams = new URLSearchParams();
    
    if (params?.skip !== undefined) searchParams.append('skip', params.skip.toString());
    if (params?.limit !== undefined) searchParams.append('limit', params.limit.toString());
    if (params?.status) searchParams.append('status', params.status);
    if (params?.priority) searchParams.append('priority', params.priority);
    if (params?.document_type) searchParams.append('document_type', params.document_type);
    if (params?.property_id) searchParams.append('property_id', params.property_id.toString());
    if (params?.requires_review !== undefined) searchParams.append('requires_review', params.requires_review.toString());
    if (params?.search) searchParams.append('search', params.search);

    const response = await api.get(`/hoa-emails/?${searchParams.toString()}`);
    return response.data;
  },

  // Get HOA email statistics
  async getStats(): Promise<HOAEmailStats> {
    const response = await api.get('/hoa-emails/stats');
    return response.data;
  },

  // Get a specific HOA email
  async getHOAEmail(id: number): Promise<HOAEmail> {
    const response = await api.get(`/hoa-emails/${id}`);
    return response.data;
  },

  // Assign an HOA email to a property
  async assignToProperty(id: number, assignment: HOAEmailAssign): Promise<HOAEmail> {
    const response = await api.post(`/hoa-emails/${id}/assign`, assignment);
    return response.data;
  },

  // Update an HOA email
  async updateHOAEmail(id: number, update: HOAEmailUpdate): Promise<HOAEmail> {
    const response = await api.put(`/hoa-emails/${id}`, update);
    return response.data;
  },

  // Delete an HOA email
  async deleteHOAEmail(id: number): Promise<void> {
    await api.delete(`/hoa-emails/${id}`);
  },

  // Get property email assignments
  async getPropertyAssignments(): Promise<PropertyEmailAssignment[]> {
    const response = await api.get('/hoa-emails/properties/assignments');
    return response.data;
  },

  // Mark as reviewed
  async markAsReviewed(id: number, notes?: string): Promise<HOAEmail> {
    return this.updateHOAEmail(id, {
      requires_review: false,
      review_notes: notes
    });
  },

  // Set priority
  async setPriority(id: number, priority: 'low' | 'medium' | 'high' | 'urgent'): Promise<HOAEmail> {
    return this.updateHOAEmail(id, { priority });
  },

  // Set status
  async setStatus(id: number, status: 'pending' | 'assigned' | 'processed' | 'ignored'): Promise<HOAEmail> {
    return this.updateHOAEmail(id, { status });
  },

  // Set document type
  async setDocumentType(id: number, document_type: DocumentType): Promise<HOAEmail> {
    return this.updateHOAEmail(id, { document_type });
  }
};